/**
 * Per-import row-sort persistence. Like column widths, a candidate-row sort is
 * a per-user view preference (not version state), so it lives in `localStorage`
 * keyed by import id. Only one column sorts at a time.
 */
import { headerKey } from '@shared/helpers/kalkylfil-csv.parser';

export type ImportSortDir = 'asc' | 'desc';
export type ImportSort = { column: string; dir: ImportSortDir };

/** Bump when the persisted shape changes (invalidates old entries). */
const STORAGE_VERSION = 1;

const storageKey = (importId: string): string =>
  `nodon.import-sort.v${STORAGE_VERSION}.${importId}`;

const isImportSort = (value: unknown): value is ImportSort => {
  if (typeof value !== 'object' || value === null) return false;
  const { column, dir } = value as Record<string, unknown>;
  return typeof column === 'string' && (dir === 'asc' || dir === 'desc');
};

/** Persisted sort for an import, or `undefined` when none is stored/parseable. */
export const loadImportSort = (importId: string): ImportSort | undefined => {
  if (typeof window === 'undefined') return undefined;
  try {
    const raw = window.localStorage.getItem(storageKey(importId));
    if (!raw) return undefined;
    const parsed: unknown = JSON.parse(raw);
    return isImportSort(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
};

/** Persist (or, with `undefined`, clear) the sort for an import. */
export const saveImportSort = (
  importId: string,
  sort: ImportSort | undefined,
): void => {
  if (typeof window === 'undefined') return;
  try {
    if (!sort) {
      window.localStorage.removeItem(storageKey(importId));
      return;
    }
    window.localStorage.setItem(storageKey(importId), JSON.stringify(sort));
  } catch {
    // Quota / disabled storage -- safe to drop the preference.
  }
};

/**
 * Three-state click cycle on a column header: a new column starts ascending,
 * the active column goes asc → desc → cleared (back to default order).
 */
export const cycleImportSort = (
  current: ImportSort | undefined,
  column: string,
): ImportSort | undefined => {
  if (!current || headerKey(current.column) !== headerKey(column)) {
    return { column, dir: 'asc' };
  }
  return current.dir === 'asc' ? { column, dir: 'desc' } : undefined;
};

/**
 * Validate a persisted sort against the import's live headers. A column that no
 * longer exists (e.g. a re-uploaded file with different columns) is ignored,
 * falling back to the default order.
 */
export const resolveImportSort = (
  sort: ImportSort | undefined,
  headers: readonly string[],
): ImportSort | undefined => {
  if (!sort) return undefined;
  return headers.some((h) => headerKey(h) === headerKey(sort.column))
    ? sort
    : undefined;
};
